// Module 4 / Slice 5b — AssignmentsTab.
//
// Reads organization.employee_assignment rows scoped to the active
// employee. The "primary" column surfaces the is_primary flag whose
// uniqueness (per overlapping date range) is enforced by the V007 GIST
// exclusion constraint (ADR 0013).
//
// Write actions (create / update / delete) are deferred to a Phase-4
// follow-up; the help-text caption explains the primary-overlap
// invariant the API enforces.
//
// PATTERNS.md §2.2 + ADR 0013.

import { useQuery } from "@tanstack/react-query";
import { workforceApiClient } from "../../../api/client";
import { useI18n } from "../../../i18n/I18nContext";

interface AssignmentsTabProps {
  employeeId: string;
}

export function AssignmentsTab({
  employeeId,
}: AssignmentsTabProps): JSX.Element {
  const { t } = useI18n();

  const query = useQuery({
    queryKey: ["workforce", "employee", employeeId, "assignments"] as const,
    queryFn: async () => {
      const response = await workforceApiClient.GET(
        "/workforce/employees/{employeeId}/assignments",
        {
          params: {
            path: { employeeId },
            query: { limit: 200 },
          },
        },
      );
      if (!response.data) {
        throw new Error("workforce_assignments_fetch_failed");
      }
      return response.data;
    },
  });

  return (
    <div data-testid="assignments-tab">
      <p className="rc-help">{t("workforce.assignments.primaryNote")}</p>

      {query.isLoading && <p>{t("login.discovering")}</p>}
      {query.isError && (
        <p role="alert" style={{ color: "var(--rc-color-danger)" }}>
          {(query.error as Error).message}
        </p>
      )}
      {query.data && query.data.items && query.data.items.length === 0 && (
        <p data-testid="assignments-empty">
          {t("workforce.assignments.empty")}
        </p>
      )}
      {query.data && query.data.items && query.data.items.length > 0 && (
        <table className="rc-table">
          <thead>
            <tr>
              <th>{t("workforce.assignments.column.unit")}</th>
              <th>{t("workforce.assignments.column.position")}</th>
              <th>{t("workforce.assignments.column.primary")}</th>
              <th>{t("workforce.assignments.column.start")}</th>
              <th>{t("workforce.assignments.column.end")}</th>
            </tr>
          </thead>
          <tbody>
            {query.data.items.map((a) => (
              <tr key={a.id} data-testid={`assignment-row-${a.id}`}>
                <td>
                  <code>{String(a.organizationUnitId).slice(0, 8)}…</code>
                </td>
                <td>
                  {a.positionKey ? <code>{a.positionKey}</code> : "\u2014"}
                </td>
                <td data-testid={`assignment-primary-${a.id}`}>
                  {a.isPrimary ? "\u2714" : "\u2014"}
                </td>
                <td>{a.effectiveStartDate}</td>
                <td>{a.effectiveEndDate ?? "\u2014"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
